import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  organizationResource,
  type CreateInviteInput,
  type CreateOrganizationInput,
  type InviteRef,
  type MemberRef,
} from './organization.resource';

export const organizationsQuery = () =>
  queryOptions({
    queryKey: ['organizations'],
    queryFn: organizationResource.list,
  });

export const membersQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'members'],
    queryFn: () => organizationResource.members(organizationId),
    select: (data) => data.members,
  });

export const invitesQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'invites'],
    queryFn: () => organizationResource.invites(organizationId),
    select: (data) => data.invites,
  });

export function useOrganizations() {
  return useQuery({ ...organizationsQuery(), select: (data) => data.organizations });
}

export function useOrganization(organizationId: string) {
  return useQuery({
    ...organizationsQuery(),
    select: (data) => data.organizations.find((org) => org.id === organizationId) ?? null,
  });
}

export function useMembers(organizationId: string | null) {
  return useQuery({
    ...membersQuery(organizationId ?? ''),
    enabled: Boolean(organizationId),
  });
}

/**
 * Nur die, denen man Erinnerungen und Schulden zuweisen oder Hinweise dazu
 * schicken kann. Freunde sehen beides nicht und fallen deshalb raus.
 */
export function useStaffMembers(organizationId: string | null) {
  const members = useMembers(organizationId);
  return {
    ...members,
    data: members.data?.filter((member) => member.role !== 'friend'),
  };
}

export function useInvites(organizationId: string | null) {
  return useQuery({
    ...invitesQuery(organizationId ?? ''),
    enabled: Boolean(organizationId),
  });
}

export function useCreateOrganization() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrganizationInput) => organizationResource.create(input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['organizations'] }),
  });
}

export function useCreateInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInviteInput) => organizationResource.createInvite(input),
    onSuccess: (_data, input) =>
      client.invalidateQueries({
        queryKey: ['organizations', input.organizationId, 'invites'],
      }),
  });
}

export function useRevokeInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ref: InviteRef) => organizationResource.revokeInvite(ref),
    onSuccess: (_data, ref) =>
      client.invalidateQueries({
        queryKey: ['organizations', ref.organizationId, 'invites'],
      }),
  });
}

export function useRemoveMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ref: MemberRef) => organizationResource.removeMember(ref),
    onSuccess: () => client.invalidateQueries({ queryKey: ['organizations'] }),
  });
}
